import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { GeneratedImage } from '@/types';
import { resolveImageUrl } from './utils';
import { useTranslation } from '@/i18n/useTranslation';

interface GalleryFilmstripProps {
  images: GeneratedImage[];
  selectedIndex: number;
  uuid: string | undefined;
  onSelect: (index: number) => void;
  filmstripRef: React.RefObject<HTMLDivElement | null>;
  thumbnailRefs: React.MutableRefObject<{ [key: number]: HTMLButtonElement | null }>;
  totalCount: number | null;
  loadingMore: boolean;
  onLoadMore: () => void;
  isDesktop: boolean;
  infoPanelVisible: boolean;
}

function FilmstripThumbnail({ 
  image, 
  index, 
  isSelected, 
  onSelect, 
  thumbnailRefs 
}: { 
  image: GeneratedImage; 
  index: number; 
  isSelected: boolean; 
  onSelect: (index: number) => void;
  thumbnailRefs: React.MutableRefObject<{ [key: number]: HTMLButtonElement | null }>;
}) {
  const [thumbLoaded, setThumbLoaded] = useState(false);

  return (
    <button
      key={image.id || image.timestamp}
      ref={(el) => {
        thumbnailRefs.current[index] = el;
      }}
      onClick={() => onSelect(index)}
      className={`relative flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all ${
        isSelected ? 'border-accent/70 opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
      }`}
    >
      {/* Skeleton loader */}
      {!thumbLoaded && (
        <div className="absolute inset-0 bg-bg-control animate-pulse" />
      )}
      
      {/* Thumbnail */}
      <img
        src={image.url}
        alt={image.filename}
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          thumbLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setThumbLoaded(true)}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          if (img.dataset.retried === 'true') {
            setThumbLoaded(true);
            return;
          }
          img.dataset.retried = 'true';
          if (!img.src.startsWith('http')) {
            img.src = resolveImageUrl(image.url);
          }
        }}
      />
    </button>
  );
}

export default function GalleryFilmstrip({
  images,
  selectedIndex,
  uuid,
  onSelect,
  filmstripRef,
  thumbnailRefs,
  totalCount,
  loadingMore,
  onLoadMore,
  isDesktop,
  infoPanelVisible,
}: GalleryFilmstripProps) {
  const { t } = useTranslation();

  return (
    <div
      ref={filmstripRef as React.RefObject<HTMLDivElement>}
      className="filmstrip flex-shrink-0 bg-bg-panel border-t border-border flex items-center gap-0 overflow-x-auto transition-all"
      style={{
        marginRight: isDesktop && infoPanelVisible ? '300px' : '0',
        paddingTop: '0.75rem',
        paddingBottom: isDesktop ? '0.75rem' : 'calc(4.75rem + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div className="flex gap-2 px-3 items-center">
        {images.map((image, index) => {
          const isSelected = uuid ? image.id === uuid : index === selectedIndex;
          return (
            <FilmstripThumbnail
              key={image.id || image.timestamp}
              image={image}
              index={index}
              isSelected={isSelected}
              onSelect={onSelect}
              thumbnailRefs={thumbnailRefs}
            />
          );
        })}
        {totalCount !== null && images.length < totalCount && (
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="flex-shrink-0 w-20 h-20 rounded-md border-2 border-dashed border-border hover:border-accent/50 flex items-center justify-center transition-colors disabled:opacity-50"
            title={t('gallery.loadMore')}
          >
            {loadingMore ? (
              <Loader2 className="w-8 h-8 text-accent animate-spin" strokeWidth={2} />
            ) : (
              <span className="text-xs text-text-secondary font-medium">{t('gallery.loadMore')}</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
